import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState } from 'react';
import { Col, Row } from 'react-grid-system';
import H1 from '../../atoms/title/H1';
import Panel from '../../atoms/Panel';
import Layout from '../../templates/Layout';
import SideBar from '../../organisms/dossier/SideBar';
import FinancialTabs from '../../organisms/dossier/FinancialTabs';
import Tabs from '../../organisms/dossier/Tabs';
import { Helmet } from 'react-helmet';
import DossierTotal from '../../organisms/table/financial/DossierTotal';
import { useTranslation } from 'react-i18next';
import SpecificationTable from '../../organisms/table/financial/SpecificationTable';
import CollapsibleTotal from '../../organisms/table/financial/CollapsableTotal';
import FinancialTableRow from '../../molecules/table/FinancialTableRow';
import { readableNumberParts } from '../../../helpers/Numbers';
export const VERDICT_TYPES = {
    AFTER: 'after_verdict',
    BEFORE: 'before_verdict',
};
function getInkassierBeforeVerdictFinancials(dossier, t) {
    return {
        initial: {
            label: 'Hoofdsom',
            value: dossier.before_verdict_specification.assigned_headsum,
        },
        costsTotal: {
            label: 'Totaal kosten',
            value: dossier.before_verdict_specification.total_costs,
        },
        dossierTotal: {
            label: 'Dossiertotaal',
            value: dossier.before_verdict_specification.total_costs_dossier,
        },
        dates: [],
        costs: [
            {
                label: 'Incasso',
                value: dossier.before_verdict_specification.collection_costs,
            },
            {
                label: 'Rente',
                value: dossier.before_verdict_specification.interest_costs,
            },
            {
                label: 'Salaris/nakosten',
                value: dossier.before_verdict_specification.after_costs,
            },
            {
                label: 'Overige proceskosten',
                value: dossier.before_verdict_specification.other_process_costs,
            },
            {
                label: 'Kosten',
                value: dossier.before_verdict_specification.costs,
            },
            {
                label: 'Overige kosten',
                value: dossier.before_verdict_specification.other_costs,
            },
        ],
        receivables: [
            {
                label: t('payments.received_by_client'),
                value: dossier.before_verdict_specification.received.client,
            },
            {
                label: t('payments.received_by_contractor'),
                value: dossier.before_verdict_specification.received.contractor,
            },
        ],
        receivedTotal: {
            label: 'Totaal ontvangsten',
            value: dossier.before_verdict_specification.received_total,
        },
        open: {
            label: 'Openstaand saldo',
            value: dossier.before_verdict_specification.balance,
        },
    };
}
function getInkassierAfterVerdictFinancials(dossier, t) {
    return {
        initial: {
            label: dossier.is_after_verdict
                ? 'Toegewezen hoofdsom'
                : 'Hoofdsom',
            value: dossier.after_verdict_specification.assigned_headsum,
        },
        costsTotal: {
            label: 'Totaal kosten',
            value: dossier.after_verdict_specification.total_costs,
        },
        dossierTotal: {
            label: 'Dossiertotaal',
            value: dossier.after_verdict_specification.total_costs_dossier,
        },
        costs: [
            {
                label: dossier.is_after_verdict ? 'Proceskosten' : 'Incasso',
                value: dossier.is_after_verdict
                    ? dossier.after_verdict_specification.process_costs
                    : dossier.after_verdict_specification.collection_costs,
            },
            {
                label: 'Rente',
                value: dossier.after_verdict_specification.interest_costs,
            },
            {
                label: 'Salaris/nakosten',
                value: dossier.after_verdict_specification.after_costs,
            },
            {
                label: dossier.is_after_verdict
                    ? 'Betekening'
                    : 'Overige proceskosten',
                value: dossier.is_after_verdict
                    ? dossier.after_verdict_specification.service_costs
                    : dossier.after_verdict_specification.other_process_costs,
            },
            {
                label: dossier.is_after_verdict ? 'Executiekosten' : 'Kosten',
                value: dossier.is_after_verdict
                    ? dossier.after_verdict_specification.execution_costs
                    : dossier.after_verdict_specification.costs,
            },
            {
                label: 'Verder termijnen',
                value: dossier.after_verdict_specification.period_costs,
            },
            {
                label: 'Overige kosten',
                value: dossier.after_verdict_specification.other_costs,
            },
        ],
        receivables: [
            {
                label: t('payments.received_by_client'),
                value: dossier.after_verdict_specification.received.client,
            },
            {
                label: t('payments.received_by_contractor'),
                value: dossier.after_verdict_specification.received.contractor,
            },
        ],
        receivedTotal: {
            label: 'Totaal ontvangsten',
            value: dossier.after_verdict_specification.received_total,
        },
        open: {
            label: 'Openstaand saldo',
            value: dossier.after_verdict_specification.balance,
        },
    };
}
const Financial = ({ dossier }) => {
    const { t } = useTranslation();
    const [currentTab, setCurrentTab] = useState(dossier.is_after_verdict ? VERDICT_TYPES.AFTER : VERDICT_TYPES.BEFORE);
    let financials = null;
    if ('closing_reason' in dossier && currentTab === VERDICT_TYPES.BEFORE) {
        financials = getInkassierBeforeVerdictFinancials(dossier, t);
    }
    if ('closing_reason' in dossier && currentTab === VERDICT_TYPES.AFTER) {
        financials = getInkassierAfterVerdictFinancials(dossier, t);
    }
    return (_jsxs(Layout, { dossierNumber: dossier.id, "data-testid": "dossier-financial", children: [_jsx(Helmet, { children: _jsxs("title", { children: ["Financieel overzicht - ", t('dossier.title')] }) }), _jsxs(H1, { "data-testid": "title", children: [t('dossier.title'), " ", dossier.id] }), _jsxs(Row, { children: [_jsx(Col, { sm: 9, children: _jsxs(Panel, { children: [_jsx(Tabs, { dossier: dossier }), 'closing_reason' in dossier && (_jsx(FinancialTabs, { currentTab: currentTab, setCurrentTab: setCurrentTab, dossier: dossier })), currentTab === VERDICT_TYPES.BEFORE &&
                                    dossier?.before_verdict_specification ? (_jsxs(CollapsibleTotal, { summaryTitle: 'Hoofdsom', summaryAmount: dossier.before_verdict_specification
                                        .calculated_headsum, children: [_jsx(FinancialTableRow, { noHover: true, label: 'Hoofdsom', amount: readableNumberParts(dossier.before_verdict_specification
                                                .assigned_headsum, 'EUR').longDecimal }), dossier.before_verdict_specification
                                            .period_costs ? (_jsx(FinancialTableRow, { noHover: true, label: 'Verdere termijnen', amount: readableNumberParts(dossier
                                                .before_verdict_specification
                                                .period_costs, 'EUR').longDecimal })) : null] })) : (_jsx(DossierTotal, { label: dossier.is_after_verdict
                                        ? 'Toegewezen hoofdsom'
                                        : 'Hoofdsom', amount: dossier.after_verdict_specification
                                        .assigned_headsum })), financials?.costs && (_jsx(SpecificationTable, { header: 'Specificatie kosten', items: financials.costs, total: financials.costsTotal })), financials?.initial && (_jsx(DossierTotal, { label: financials.dossierTotal.label, amount: financials.dossierTotal.value })), financials?.receivables && (_jsx(SpecificationTable, { header: 'Ontvangen betalingen', items: financials.receivables, total: financials.receivedTotal })), financials?.open && (_jsx(DossierTotal, { label: financials.open.label, amount: parseFloat(financials.open.value + ''), highlighted: true }))] }) }), _jsx(Col, { sm: 3, children: _jsx(SideBar, { dossier: dossier }) })] })] }));
};
export default Financial;
